import React, { useCallback, useEffect, useState } from 'react'
import { Modal, Table } from 'antd'
import { toast } from 'react-toastify'
import Loader from '../../../pages/pages/loader'
import { useLazyGetCampaignInfoQuery } from '../../../redux/apis/campaignsManagement/campaigns/cMCampaignsApiSlice'

const renderLink = (url) => {
  if (!url) return '-'
  return (
    <a href={url} target='_blank' rel='noreferrer' className='underline break-all'>
      {url}
    </a>
  )
}

const formatCampaignForCopy = (row) =>
  [
    `Works Name: ${row.campaign_name ?? ''}`,
    `Authorization Letter: ${row.authorization_letter ?? '-'}`,
    `Sample of the Work: ${row.sample_of_work ?? ''}`,
  ].join('\n')

const columns = [
  {
    title: 'Works Name',
    dataIndex: 'campaign_name',
    key: 'campaign_name',
  },
  {
    title: 'Authorization Letter',
    dataIndex: 'authorization_letter',
    key: 'authorization_letter',
    render: renderLink,
  },
  {
    title: 'Sample of the Work',
    dataIndex: 'sample_of_work',
    key: 'sample_of_work',
    render: renderLink,
  },
]

const CampaignInfoModal = ({ open, onClose, campaignName }) => {
  const [results, setResults] = useState([])
  const [fetchCampaignInfo, { isFetching, error }] = useLazyGetCampaignInfoQuery()

  const loadCampaignInfo = useCallback(async (name) => {
    if (!name?.trim()) return
    try {
      const response = await fetchCampaignInfo({ search: name.trim() }).unwrap()
      setResults(response?.data ?? [])
    } catch {
      setResults([])
    }
  }, [fetchCampaignInfo])

  useEffect(() => {
    if (open && campaignName) {
      loadCampaignInfo(campaignName)
    }
  }, [open, campaignName, loadCampaignInfo])

  useEffect(() => {
    if (error) {
      const message =
        error?.data?.message || error?.data?.errors || 'Failed to load campaign info'
      toast.error(typeof message === 'string' ? message : 'Failed to load campaign info')
    }
  }, [error])

  const handleClose = () => {
    setResults([])
    onClose()
  }

  const handleCopy = async () => {
    if (!results.length) {
      toast.warning('No data to copy')
      return
    }
    const text = results.map(formatCampaignForCopy).join('\n\n')
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard')
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title='Campaign Info'
      className='custom-modal'
      width={900}
      footer={
        <div className='d-flex justify-content-end gap-2'>
          <button
            className='btn btn-outline-theme'
            onClick={handleCopy}
            disabled={!results.length}
          >
            <i className='fas fa-copy me-1' />
            Copy
          </button>
          <button className='btn btn-theme' onClick={handleClose}>
            Close
          </button>
        </div>
      }
    >
      <Loader isLoading={isFetching} />
      {campaignName && (
        <p className='mb-3 fw-semibold text-white text-lg'>{campaignName}</p>
      )}
      <Table
        className='tableContainer'
        columns={columns}
        dataSource={results}
        rowKey={(record, index) => `${record.campaign_name}-${index}`}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No results found' }}
      />
    </Modal>
  )
}

export default CampaignInfoModal
